// ============================================================================
// AdminCpa52Panel — CPA-52 conservation-plan submissions (Build S6, Task #26).
// ============================================================================
// Sidebar entry: #admin/cpa52. UIF only.
//
// A CPA-52 submission is a signed conservation-plan document that bundles one
// or more practice instances on a farm. Its lifecycle runs
//   draft → submitted → signed → active → superseded/void
// and the server enforces the legal transitions. Moving into 'signed' or
// 'active' requires an executed document to be attached.
//
// Three surfaces:
//   1. Filter bar: farm_id + status. Loads adminCpa52List on mount/change.
//   2. Table: one row per submission, "Open" opens the detail modal.
//   3. Modals: New draft (with a practice picker) and Detail (read-only
//      fields + editable metadata + status transitions).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Plus, RefreshCw, AlertCircle, CheckCircle2, FileText,
  X as XIcon,
} from 'lucide-react';
import {
  api,
  type Cpa52Submission,
  type Cpa52SubmissionPractice,
  type Cpa52Status,
  type PracticeInstance,
} from '../api';

interface Props {
  token: string;
}

const STATUSES: Cpa52Status[] = ['draft', 'submitted', 'signed', 'active', 'superseded', 'void'];

// Pill classes per lifecycle state.
function statusTone(status: Cpa52Status): string {
  switch (status) {
    case 'draft':      return 'bg-gray-100 border-gray-200 text-gray-700';
    case 'submitted':  return 'bg-blue-50 border-blue-200 text-blue-800';
    case 'signed':     return 'bg-amber-50 border-amber-200 text-amber-800';
    case 'active':     return 'bg-emerald-50 border-emerald-200 text-emerald-800';
    case 'superseded': return 'bg-slate-100 border-slate-200 text-slate-700';
    case 'void':       return 'bg-red-50 border-red-200 text-red-800';
  }
}

function StatusPill({ status }: { status: Cpa52Status }) {
  return (
    <span className={'inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ' + statusTone(status)}>
      {status}
    </span>
  );
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function YesNo({ on }: { on: boolean }) {
  return on
    ? <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="yes" />
    : <span className="text-gray-400">—</span>;
}

export function AdminCpa52Panel({ token }: Props) {
  const [submissions, setSubmissions] = useState<Cpa52Submission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [farmIdFilter, setFarmIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    const farmId = farmIdFilter.trim() ? Number(farmIdFilter.trim()) : undefined;
    api.adminCpa52List(token, {
      farm_id: farmId != null && !isNaN(farmId) ? farmId : undefined,
      status: statusFilter !== 'all' ? (statusFilter as Cpa52Status) : undefined,
    })
      .then((d) => setSubmissions(d.submissions ?? []))
      .catch((err: any) => setError(err?.message || 'Could not load CPA-52 submissions.'))
      .finally(() => setLoading(false));
  }, [token, farmIdFilter, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CPA-52 Submissions</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            A CPA-52 is a signed conservation-plan document that bundles one or more practice
            instances on a farm. Track each submission from draft through signature to an active plan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
          >
            <Plus className="h-4 w-4" />
            New draft
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Farm ID</span>
          <input
            type="number"
            value={farmIdFilter}
            onChange={(e) => setFarmIdFilter(e.target.value)}
            placeholder="All farms"
            className="w-36 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          >
            <option value="all">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2">Submission #</th>
              <th className="px-3 py-2">Farm</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Practices</th>
              <th className="px-3 py-2">Effective date</th>
              <th className="px-3 py-2 text-center">Envelope</th>
              <th className="px-3 py-2 text-center">Executed doc</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-gray-500 text-center"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading…</td></tr>
            )}
            {!loading && submissions.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-gray-500 italic text-center">No submissions match this filter.</td></tr>
            )}
            {submissions.map((s) => (
              <tr key={s.cpa52_submission_id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-900">
                  {s.submission_number || '—'}
                  <span className="ml-1.5 text-[10px] text-gray-400 font-mono">#{s.cpa52_submission_id.slice(0, 8)}</span>
                </td>
                <td className="px-3 py-2 text-gray-700 font-mono text-xs">#{s.farm_id}</td>
                <td className="px-3 py-2"><StatusPill status={s.status} /></td>
                <td className="px-3 py-2 text-right text-gray-700">{s.practice_count ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(s.effective_date)}</td>
                <td className="px-3 py-2 text-center"><div className="flex justify-center"><YesNo on={!!s.envelope_id} /></div></td>
                <td className="px-3 py-2 text-center"><div className="flex justify-center"><YesNo on={!!s.executed_document_id} /></div></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setDetailId(s.cpa52_submission_id)}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreateModal
          token={token}
          initialFarmId={farmIdFilter.trim()}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); refresh(); }}
        />
      )}
      {detailId && (
        <DetailModal
          token={token}
          id={detailId}
          onClose={() => setDetailId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

// ============================================================================
// New draft modal — collect submission metadata + pick practice instances
// ============================================================================
function CreateModal({ token, initialFarmId, onClose, onSaved }: {
  token: string;
  initialFarmId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [farmId, setFarmId] = useState(initialFarmId);
  const [enrollmentId, setEnrollmentId] = useState('');
  const [submissionNumber, setSubmissionNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Practice picker — driven by the farm_id above.
  const [practices, setPractices] = useState<PracticeInstance[]>([]);
  const [practicesLoaded, setPracticesLoaded] = useState(false);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [practicesError, setPracticesError] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  function onFarmIdChange(v: string) {
    setFarmId(v);
    // Loaded practices belong to the previous farm — clear so we never submit
    // instances from a different farm (which the server rejects as a mismatch).
    setPractices([]);
    setPracticesLoaded(false);
    setPracticesError('');
    setSelected(new Set());
  }

  async function loadPractices() {
    const fid = Number(farmId.trim());
    if (!farmId.trim() || isNaN(fid)) {
      setPracticesError('Enter a farm ID first.');
      return;
    }
    setLoadingPractices(true);
    setPracticesError('');
    try {
      const d = await api.adminPractices(token, { farm_id: fid });
      setPractices(d.practices ?? []);
      setPracticesLoaded(true);
    } catch (err: any) {
      setPracticesError(err?.message || 'Could not load practices.');
    } finally {
      setLoadingPractices(false);
    }
  }

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const fid = Number(farmId.trim());
    if (!farmId.trim() || isNaN(fid)) { setError('A valid farm ID is required.'); return; }
    if (!enrollmentId.trim()) { setError('An enrollment ID is required.'); return; }
    if (selected.size === 0) { setError('Select at least one practice instance.'); return; }
    setBusy(true); setError('');
    try {
      await api.adminCpa52Create(token, {
        farm_id: fid,
        enrollment_id: enrollmentId.trim(),
        practice_instance_ids: Array.from(selected),
        submission_number: submissionNumber.trim() || null,
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (err: any) {
      // Surfaces 409 practice_mismatch (a practice belongs to another farm /
      // enrollment) and any other server-side validation message.
      setError(err?.message || 'Could not create submission.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="New CPA-52 draft" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Farm ID</span>
            <input
              type="number"
              required
              value={farmId}
              onChange={(e) => onFarmIdChange(e.target.value)}
              disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Submission # (optional)</span>
            <input
              type="text"
              value={submissionNumber}
              onChange={(e) => setSubmissionNumber(e.target.value)}
              disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Enrollment ID (UUID)</span>
          <input
            type="text"
            required
            value={enrollmentId}
            onChange={(e) => setEnrollmentId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>

        {/* Practice picker */}
        <div className="border border-gray-200 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-gray-700">
              Practice instances {selected.size > 0 && <span className="text-uif-green-dark">({selected.size} selected)</span>}
            </span>
            <button
              type="button"
              onClick={loadPractices}
              disabled={busy || loadingPractices || !farmId.trim()}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loadingPractices ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Load practices
            </button>
          </div>
          {practicesError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-xs">{practicesError}</div>
          )}
          {practicesLoaded && practices.length === 0 && !practicesError && (
            <p className="text-xs text-gray-500 italic">No practice instances found for this farm.</p>
          )}
          {!practicesLoaded && !practicesError && (
            <p className="text-xs text-gray-500 italic">Enter a farm ID and click “Load practices” to choose instances to bundle.</p>
          )}
          {practices.length > 0 && (
            <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
              {practices.map((p) => (
                <label key={p.practice_instance_id} className="flex items-start gap-2 py-1.5 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.has(p.practice_instance_id)}
                    onChange={() => toggle(p.practice_instance_id)}
                    disabled={busy}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">{p.nrcs_code ?? '—'}</span>
                    {' — '}
                    {p.practice_name ?? p.practice_instance_id}
                    {' '}
                    <span className="text-gray-400">({p.status})</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || !farmId.trim() || !enrollmentId.trim() || selected.size === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create draft
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Detail modal — read-only fields, editable metadata, status transitions
// ============================================================================
function DetailModal({ token, id, onClose, onChanged }: {
  token: string;
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [submission, setSubmission] = useState<Cpa52Submission | null>(null);
  const [transitions, setTransitions] = useState<Cpa52Status[]>([]);
  const [practices, setPractices] = useState<Cpa52SubmissionPractice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Editable metadata
  const [subNum, setSubNum] = useState('');
  const [notes, setNotes] = useState('');
  const [envId, setEnvId] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [metaError, setMetaError] = useState('');

  // Transition
  const [toStatus, setToStatus] = useState<Cpa52Status | ''>('');
  const [tEnvId, setTEnvId] = useState('');
  const [tExecDoc, setTExecDoc] = useState('');
  const [tEffDate, setTEffDate] = useState('');
  const [tSubNum, setTSubNum] = useState('');
  const [tNotes, setTNotes] = useState('');
  const [transitioning, setTransitioning] = useState(false);
  const [transError, setTransError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    api.adminCpa52Detail(token, id)
      .then((d) => {
        setSubmission(d.submission);
        setTransitions(d.allowed_transitions ?? []);
        setPractices(d.practices ?? []);
        setSubNum(d.submission.submission_number ?? '');
        setNotes(d.submission.notes ?? '');
        setEnvId(d.submission.envelope_id ?? '');
        setToStatus((d.allowed_transitions ?? [])[0] ?? '');
      })
      .catch((err: any) => setLoadError(err?.message || 'Could not load submission.'))
      .finally(() => setLoading(false));
  }, [token, id]);

  useEffect(() => { load(); }, [load]);

  async function saveMeta(e: React.FormEvent) {
    e.preventDefault();
    if (savingMeta) return;
    setSavingMeta(true); setMetaError('');
    try {
      await api.adminCpa52Patch(token, id, {
        submission_number: subNum.trim() || null,
        notes: notes.trim() || null,
        ...(envId.trim() ? { envelope_id: envId.trim() } : {}),
      });
      onChanged();
      load();
    } catch (err: any) {
      setMetaError(err?.message || 'Could not save metadata.');
    } finally {
      setSavingMeta(false);
    }
  }

  async function doTransition(e: React.FormEvent) {
    e.preventDefault();
    if (transitioning || !toStatus || !submission) return;
    // Client-side guard: signing/activating requires an executed document,
    // either already attached to the submission or entered here.
    const requiresDoc = toStatus === 'signed' || toStatus === 'active';
    const effectiveDoc = tExecDoc.trim() || submission.executed_document_id;
    if (requiresDoc && !effectiveDoc) {
      setTransError(`Moving to “${toStatus}” requires an executed document. Enter an executed document ID below (or set one on the submission first).`);
      return;
    }
    setTransitioning(true); setTransError('');
    try {
      await api.adminCpa52Transition(token, id, {
        to_status: toStatus,
        ...(tEnvId.trim() ? { envelope_id: tEnvId.trim() } : {}),
        ...(tExecDoc.trim() ? { executed_document_id: tExecDoc.trim() } : {}),
        ...(tEffDate ? { effective_date: tEffDate } : {}),
        ...(tSubNum.trim() ? { submission_number: tSubNum.trim() } : {}),
        ...(tNotes.trim() ? { notes: tNotes.trim() } : {}),
      });
      // Clear the transition inputs, then reload the (now-advanced) submission.
      setTEnvId(''); setTExecDoc(''); setTEffDate(''); setTSubNum(''); setTNotes('');
      onChanged();
      load();
    } catch (err: any) {
      // Surfaces 409 check_failed / illegal_transition messages from the server.
      setTransError(err?.message || 'Transition failed.');
    } finally {
      setTransitioning(false);
    }
  }

  return (
    <ModalShell title="CPA-52 submission" onClose={onClose}>
      {loading && (
        <div className="py-8 text-center text-gray-500"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading…</div>
      )}
      {!loading && loadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{loadError}</div>
        </div>
      )}
      {!loading && !loadError && submission && (
        <div className="space-y-5">
          {/* Header line */}
          <div className="flex items-center gap-2">
            <StatusPill status={submission.status} />
            <span className="text-sm font-semibold text-gray-900">{submission.submission_number || 'Unnumbered'}</span>
            <span className="text-[10px] text-gray-400 font-mono">#{submission.cpa52_submission_id.slice(0, 8)}</span>
          </div>

          {/* Read-only fields */}
          <div className="rounded-md border border-gray-200 divide-y divide-gray-100 px-3">
            <ReadRow label="Submission ID"><span className="font-mono">{submission.cpa52_submission_id}</span></ReadRow>
            <ReadRow label="Farm"><span className="font-mono">#{submission.farm_id}</span></ReadRow>
            <ReadRow label="Enrollment ID"><span className="font-mono">{submission.enrollment_id}</span></ReadRow>
            <ReadRow label="Envelope ID">{submission.envelope_id ? <span className="font-mono">{submission.envelope_id}</span> : '—'}</ReadRow>
            <ReadRow label="Executed document">{submission.executed_document_id ? <span className="font-mono">{submission.executed_document_id}</span> : '—'}</ReadRow>
            <ReadRow label="Effective date">{fmtDate(submission.effective_date)}</ReadRow>
            <ReadRow label="Submitted at">{fmtDate(submission.submitted_at)}</ReadRow>
            <ReadRow label="Signed at">{fmtDate(submission.signed_at)}</ReadRow>
            <ReadRow label="Practices">{submission.practice_count ?? practices.length}</ReadRow>
          </div>

          {/* Bundled practices */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Bundled practices</h3>
            {practices.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No practices attached.</p>
            ) : (
              <ul className="rounded-md border border-gray-200 divide-y divide-gray-100">
                {practices.map((p) => (
                  <li key={p.submission_practice_id} className="px-3 py-2 text-xs text-gray-700 flex items-center justify-between gap-2">
                    <span>
                      <span className="font-medium">{p.nrcs_code ?? '—'}</span>
                      {' — '}
                      {p.practice_name ?? p.practice_instance_id}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-gray-400">{p.role}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Editable metadata */}
          <form onSubmit={saveMeta} className="border-t border-gray-200 pt-4 space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Edit metadata</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Submission #</span>
                <input type="text" value={subNum} onChange={(e) => setSubNum(e.target.value)} disabled={savingMeta}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Envelope ID (UUID)</span>
                <input type="text" value={envId} onChange={(e) => setEnvId(e.target.value)} disabled={savingMeta}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Notes</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} disabled={savingMeta}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
            </label>
            {metaError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{metaError}</div>
            )}
            <div className="flex justify-end">
              <button type="submit" disabled={savingMeta}
                className="inline-flex items-center gap-1.5 rounded-md border border-uif-green text-uif-green-dark font-semibold px-4 py-2 text-sm hover:bg-uif-green/5 disabled:opacity-50">
                {savingMeta ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Save metadata
              </button>
            </div>
          </form>

          {/* Transition */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Advance status</h3>
            {transitions.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No transitions available from “{submission.status}”.</p>
            ) : (
              <form onSubmit={doTransition} className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Transition to</span>
                  <select
                    value={toStatus}
                    onChange={(e) => setToStatus(e.target.value as Cpa52Status)}
                    disabled={transitioning}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                  >
                    {transitions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
                {(toStatus === 'signed' || toStatus === 'active') && !submission.executed_document_id && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
                    Moving to “{toStatus}” requires an executed document. Enter an executed document ID below.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-700 mb-1">Executed document ID (UUID)</span>
                    <input type="text" value={tExecDoc} onChange={(e) => setTExecDoc(e.target.value)} disabled={transitioning}
                      placeholder={submission.executed_document_id ?? ''}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-700 mb-1">Envelope ID (UUID)</span>
                    <input type="text" value={tEnvId} onChange={(e) => setTEnvId(e.target.value)} disabled={transitioning}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-700 mb-1">Effective date</span>
                    <input type="date" value={tEffDate} onChange={(e) => setTEffDate(e.target.value)} disabled={transitioning}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-700 mb-1">Submission #</span>
                    <input type="text" value={tSubNum} onChange={(e) => setTSubNum(e.target.value)} disabled={transitioning}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                  </label>
                </div>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Notes</span>
                  <textarea value={tNotes} onChange={(e) => setTNotes(e.target.value)} rows={2} disabled={transitioning}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                </label>
                {transError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{transError}</div>
                )}
                <div className="flex justify-end">
                  <button type="submit" disabled={transitioning || !toStatus}
                    className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
                    {transitioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Apply transition
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function ReadRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1.5">
      <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-xs text-gray-800 text-right break-all">{children}</span>
    </div>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-uif-green-dark">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
